import { callOutcomeLabel } from "@/lib/callOutcomeLabel";
import { Select, SelectPaginateAsync } from "@/components/atoms/Select";
import CallRecordingPlayer from "@/components/molecules/CallRecordingPlayer";
import CallMeetingResult from "@/components/molecules/cards/callLogs/CallMeetingResult";
import CallStatsBarChart from "@/components/molecules/charts/CallStatsBarChart";
import AllCallLogsHeader from "@/components/molecules/table-headers/AllCallLogsHeader";
import Table from "@/components/molecules/table/Table";
import { callActorLabel } from "@/lib/callActorLabel";
import { callNumber } from "@/lib/callNumber";
import CallLog, {
  CallMeetingResult as MeetingResult,
} from "@/interfaces/CallLog";
import User from "@/interfaces/IUser";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import useLoadOptions from "@/hooks/useLoadOptions";
import { useTableState } from "@/hooks/useTableState";
import {
  useGetCallLogUsersQuery,
  useGetCallLogsQuery,
  useGetCallStatsQuery,
} from "@/services/callLogs";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { FC, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { SingleValue } from "react-select";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const correspondentLabel = (row: CallLog): string => {
  if (row.matchedCompany) return row.matchedCompany.name;
  if (row.matchedPerson)
    return `${row.matchedPerson.firstname} ${row.matchedPerson.lastname}`;
  if (row.matchedInstructor)
    return `${row.matchedInstructor.firstname} ${row.matchedInstructor.lastname}`;
  return callNumber(row.remoteNumber) ?? "Numéro indisponible";
};

const correspondentLink = (row: CallLog): string | null => {
  if (row.matchedCompany) return `/companies/${row.matchedCompany.id}`;
  if (row.matchedPerson) return `/people/${row.matchedPerson.id}`;
  if (row.matchedInstructor) return `/instructors/${row.matchedInstructor.id}`;
  return null;
};

const formatDuration = (totalSeconds: number | null): string => {
  if (totalSeconds == null) return "—";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const PERIOD_OPTIONS: { label: string; days: number }[] = [
  { label: "Aujourd'hui", days: 1 },
  { label: "7 derniers jours", days: 7 },
  { label: "30 derniers jours", days: 30 },
];

const accountOptionLabel = (user: User): string =>
  `${user.fullname} — ${user.organisation?.name ?? "Sans organisme"} (${user.email})`;

const CallLineAccounts = ({ call }: { call: CallLog }) => {
  const line = call.softphoneLine;
  // Legacy membership rows can be stale. Shared lines use only their members,
  // never the technical owner retained for recordings.
  const accounts =
    line?.protocolVersion === 2 ? line.users : line?.user ? [line.user] : [];
  return (
    <div>
      {!line
        ? "Ligne indisponible"
        : !accounts
          ? "Comptes indisponibles"
          : accounts.length
            ? accounts.map((user) => (
                <div key={user.id}>
                  {[user.fullname, user.organisation?.name]
                    .filter(Boolean)
                    .join(" — ")}
                </div>
              ))
            : "Aucun compte associé"}
      {call.actorAttribution === "verified" && call.actorName && (
        <div className="text-muted fs--1">
          Compte utilisé : {callActorLabel(call)}
        </div>
      )}
      {call.actorAttribution === "unanswered" && (
        <div className="text-muted fs--1">Non décroché</div>
      )}
    </div>
  );
};

const CallLogsView: FC = () => {
  const currentUser = useSelector(selectCurrentUser);
  const isAdmin = checkIfUserHasRoles(currentUser, ["ADMIN", "CSM"]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [periodDays, setPeriodDays] = useState<number>(7);
  const [meetingResult, setMeetingResult] = useState<MeetingResult | "">("");
  const { loadUsersOptions } = useLoadOptions();
  // A team lead filters among their team members; admins use the full user list.
  const { data: teamMembers = [] } = useGetCallLogUsersQuery(undefined, {
    skip: isAdmin,
  });
  const canFilterByUser = isAdmin || teamMembers.length > 0;

  const [state, setState] = useTableState<Partial<TableState>>("callLogs", {
    pagination: { pageIndex: 0, pageSize: 15 },
    globalFilter: "",
  });

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const resetPage = () =>
    setState((previous) => ({
      ...previous,
      pagination: {
        pageSize: previous.pagination?.pageSize ?? 15,
        pageIndex: 0,
      },
    }));
  const changeUser = (user: SingleValue<User>) => {
    setSelectedUser(user);
    resetPage();
  };

  const scopeFilter = {
    ...(canFilterByUser && selectedUser ? { userId: selectedUser.id } : {}),
  };

  const listQuery = useGetCallLogsQuery(
    {
      ...state.pagination,
      query: debouncedQuery,
      ...scopeFilter,
      days: periodDays,
      meetingResult: meetingResult || undefined,
    },
    { pollingInterval: 30000, refetchOnFocus: true, refetchOnReconnect: true },
  );

  const statsQuery = useGetCallStatsQuery(
    {
      days: periodDays,
      ...scopeFilter,
      query: debouncedQuery,
      meetingResult: meetingResult || undefined,
    },
    { pollingInterval: 30000, refetchOnFocus: true, refetchOnReconnect: true },
  );
  const isStatsFetching = statsQuery.isFetching;
  const stats = !statsQuery.isError
    ? (statsQuery.currentData ??
      (!isStatsFetching ? statsQuery.data : undefined))
    : undefined;
  const data = !listQuery.isError
    ? (listQuery.currentData ??
      (!listQuery.isFetching ? listQuery.data : undefined))
    : undefined;

  const columns = useMemo<ColumnDef<CallLog>[]>(
    () => [
      {
        header: "Date",
        accessorFn: (row) =>
          DateTime.fromISO(row.startedAt ?? row.createdAt)
            .setZone("Europe/Paris")
            .toFormat("dd-MM-yyyy HH:mm"),
        id: "createdAt",
        cell: (info) => info.getValue(),
      },
      {
        header: "Ligne",
        accessorFn: (row) => row.softphoneLine?.label ?? row.extension ?? "—",
        id: "line",
        cell: (info) => info.getValue(),
      },
      {
        header: "Comptes de la ligne",
        id: "lineAccounts",
        enableSorting: false,
        cell: (info) => <CallLineAccounts call={info.row.original} />,
      },
      {
        header: "Sens",
        accessorFn: (row) =>
          row.direction === "in"
            ? "Entrant"
            : row.direction === "out"
              ? "Sortant"
              : "—",
        id: "direction",
        cell: (info) => info.getValue(),
      },
      {
        header: "Correspondant",
        accessorFn: correspondentLabel,
        id: "correspondent",
        cell: (info) => {
          const to = correspondentLink(info.row.original);
          const label = info.getValue() as string;
          return to ? <Link to={to}>{label}</Link> : label;
        },
      },
      {
        header: "Durée",
        accessorFn: (row) =>
          row.durationS != null ? `${row.durationS}s` : "—",
        id: "durationS",
        cell: (info) => info.getValue(),
      },
      {
        header: "Statut",
        accessorFn: (row) => callOutcomeLabel(row),
        id: "outcome",
        cell: (info) => info.getValue(),
      },
      {
        header: "Résultat rendez-vous",
        id: "meetingAttribution",
        enableSorting: false,
        cell: (info) => (
          <CallMeetingResult result={info.row.original.meetingAttribution} />
        ),
      },
      {
        header: "Enregistrement",
        accessorFn: (row) =>
          row.recordingFile ? (row.recordingFile.link ?? "") : "",
        id: "recording",
        cell: (info) =>
          info.getValue() ? (
            <CallRecordingPlayer src={info.getValue() as string} />
          ) : (
            "—"
          ),
      },
    ],
    [],
  );

  return (
    <div className="container-fluid py-4 formalys-page formalys-call-logs-page">
      <Row className="mb-3 align-items-center">
        <Col>
          <p className="formalys-eyebrow mb-2">Téléphonie</p>
          <h1 className="mb-2">Journal des appels</h1>
          <p className="text-muted mb-0">
            Suivez les échanges de votre équipe et retrouvez chaque
            correspondant.
          </p>
          <p className="text-muted fs--1 mb-0">
            Les comptes affichés sont ceux actuellement associés à la ligne. Le
            compte utilisé lors de l’appel est précisé uniquement lorsqu’il est
            identifié.
          </p>
        </Col>
        <Col xs="auto">
          <div className="btn-group" role="group" aria-label="Période">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.days}
                type="button"
                className={`btn btn-sm ${
                  periodDays === option.days
                    ? "btn-primary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => {
                  setPeriodDays(option.days);
                  resetPage();
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Col>
      </Row>

      {canFilterByUser && (
        <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
          <Form.Label htmlFor="call-line-account">
            Compte associé à la ligne
          </Form.Label>
          {isAdmin ? (
            <SelectPaginateAsync
              inputId="call-line-account"
              aria-label="Compte associé à la ligne"
              loadOptions={loadUsersOptions}
              getOptionLabel={accountOptionLabel}
              getOptionValue={(option: User) => option.id.toString()}
              value={selectedUser}
              isMulti={false}
              isClearable
              placeholder="Sélectionner..."
              onChange={(user) =>
                changeUser((user as SingleValue<User>) ?? null)
              }
            />
          ) : (
            <Select
              inputId="call-line-account"
              aria-label="Compte associé à la ligne"
              options={teamMembers}
              getOptionLabel={accountOptionLabel}
              getOptionValue={(option: User) => option.id.toString()}
              value={selectedUser}
              isMulti={false}
              isClearable
              placeholder="Toute mon équipe"
              onChange={(user) =>
                changeUser((user as SingleValue<User>) ?? null)
              }
            />
          )}
        </Form.Group>
      )}

      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card className="h-100 formalys-stat-card">
            <Card.Body>
              <div className="text-muted fs--1">Nombre d'appels</div>
              <div className="fs-2 fw-bold">
                {isStatsFetching ? <Spinner size="sm" /> : (stats?.total ?? 0)}
              </div>
              <div className="d-flex gap-3 fs--1 text-muted mt-2">
                <span>
                  Entrants&nbsp;
                  <strong className="text-body">{stats?.inbound ?? 0}</strong>
                </span>
                <span>
                  Sortants&nbsp;
                  <strong className="text-body">{stats?.outbound ?? 0}</strong>
                </span>
                <span>
                  Manqués&nbsp;
                  <strong className="text-body">{stats?.missed ?? 0}</strong>
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 formalys-stat-card">
            <Card.Body>
              <div className="text-muted fs--1">Durée d'appels</div>
              <div className="fs-2 fw-bold">
                {isStatsFetching ? (
                  <Spinner size="sm" />
                ) : (
                  formatDuration(stats?.totalDurationS ?? 0)
                )}
              </div>
              <div className="d-flex gap-3 fs--1 text-muted mt-2">
                <span>
                  Moy. entrants&nbsp;
                  <strong className="text-body">
                    {formatDuration(stats?.avgDurationInboundS ?? null)}
                  </strong>
                </span>
                <span>
                  Moy. sortants&nbsp;
                  <strong className="text-body">
                    {formatDuration(stats?.avgDurationOutboundS ?? null)}
                  </strong>
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 formalys-stat-card">
            <Card.Body>
              <div className="text-muted fs--1">Appels en cours</div>
              <div className="fs-2 fw-bold">
                {isStatsFetching ? (
                  <Spinner size="sm" />
                ) : (
                  (stats?.inProgress ?? 0)
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <h2 className="h5 mb-3">Rendez-vous issus des appels</h2>
          <Row className="g-3">
            <Col sm={6} xl={3}>
              <div className="fs-2 fw-bold">{stats?.answered ?? "—"}</div>
              <div>Appels décrochés</div>
            </Col>
            <Col sm={6} xl={3}>
              <div className="fs-2 fw-bold text-primary">
                {stats?.callsWithMeetings ?? "—"}
              </div>
              <div>Appels avec rendez-vous</div>
              <small className="text-muted">
                {stats?.attributedMeetings ?? "—"} rendez-vous associés
              </small>
            </Col>
            <Col sm={6} xl={3}>
              <div className="fs-2 fw-bold text-primary">
                {stats?.conversionRate == null
                  ? "—"
                  : `${stats.conversionRate.toLocaleString("fr-FR")} %`}
              </div>
              <div>Taux de transformation</div>
              <small className="text-muted">
                Appels avec RDV / appels décrochés
              </small>
            </Col>
            <Col sm={6} xl={3}>
              <div className="fs-2 fw-bold">
                {stats?.awaitingMeetings ?? "—"}
              </div>
              <div>Appels encore dans les 24 h</div>
              <small className="text-muted">Sans RDV associé à ce stade</small>
            </Col>
          </Row>
          <p className="text-muted fs--1 mt-3 mb-0">
            Dernier appel décroché du même client, avec RDV créé pendant l’appel
            ou dans les 24 h après sa fin. Un appel avec plusieurs RDV compte
            une fois dans le taux. Les résultats peuvent évoluer pendant ce
            délai, y compris pour un RDV créé après la période affichée.
            Horaires de Paris ; attribution selon les horaires enregistrés.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-3 formalys-chart-card">
        <Card.Body>
          <div className="fs--1 text-muted mb-2">
            Évolution du nombre d'appels pour la période sélectionnée
          </div>
          {isStatsFetching && !stats ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <CallStatsBarChart series={stats?.series ?? []} />
          )}
        </Card.Body>
      </Card>

      <Form.Group
        className="mb-3"
        controlId="call-meeting-result"
        style={{ maxWidth: 360 }}
      >
        <Form.Label>Résultat rendez-vous</Form.Label>
        <Form.Select
          value={meetingResult}
          onChange={(e) => {
            setMeetingResult(e.target.value as MeetingResult | "");
            resetPage();
          }}
        >
          <option value="">Tous les résultats</option>
          <option value="with_meeting">Avec rendez-vous</option>
          <option value="pending">Sans RDV · délai en cours</option>
          <option value="without_meeting">Sans rendez-vous après 24 h</option>
          <option value="ineligible">Appels non décrochés</option>
          <option value="unidentified">Client non identifié</option>
        </Form.Select>
      </Form.Group>
      {(listQuery.isError || statsQuery.isError) && (
        <Alert variant="danger">
          Impossible de charger les appels ou leurs statistiques.{" "}
          <Button
            variant="link"
            onClick={() => {
              listQuery.refetch();
              statsQuery.refetch();
            }}
          >
            Réessayer
          </Button>
        </Alert>
      )}
      <Table
        HeaderComponent={AllCallLogsHeader}
        columns={columns}
        state={state}
        tableName="callLogs"
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
        isLoading={listQuery.isFetching && !listQuery.currentData}
      />
    </div>
  );
};

export default CallLogsView;
